import { useState, useEffect } from 'react';
import { updateItem, FIG_LISTS } from '../../api/figApi';
import generateACORD28 from './generateACORD28';
import { isCurrentPolicy } from '../../utils/policySelect';
import './FIG.css';

const YNN = ['', 'Yes', 'No', 'N/A'];

function ACORD28({ insured, policies, buildings, additionalInterests, coverages, getToken, selectedPolicyId, onDirtyChange, onBack }) {
  // Filter CP policies. Choose from current (Active/Bound) rows so prior-term
  // history never seeds the form; input order is kept. Falls back to every CP
  // row only when none is current.
  const allCP = (policies || []).filter(p =>
    (p.LOB || '').toLowerCase().includes('property')
  );
  const currentCP = allCP.filter(isCurrentPolicy);
  const cpPolicies = currentCP.length > 0 ? currentCP : allCP;

  // Use sidebar-selected policy if it's a CP policy, otherwise default to first CP
  const sidebarCPIdx = selectedPolicyId ? cpPolicies.findIndex(p => p.id === selectedPolicyId) : -1;
  const [selectedPolicyIdx, setSelectedPolicyIdx] = useState(sidebarCPIdx >= 0 ? sidebarCPIdx : 0);
  const [selectedBuildingIdx, setSelectedBuildingIdx] = useState(0);

  // Sync with sidebar selection changes
  useEffect(() => {
    if (selectedPolicyId) {
      const idx = cpPolicies.findIndex(p => p.id === selectedPolicyId);
      if (idx >= 0) setSelectedPolicyIdx(idx);
    }
  }, [selectedPolicyId]);

  const policy = cpPolicies[selectedPolicyIdx] || {};
  const building = (buildings || [])[selectedBuildingIdx] || {};

  // A record is a property interest when it carries at least one ACORD 28
  // designation. This replaced an exact-match test against InterestType, which
  // silently dropped anything phrased differently ("Mortgagee and Lenders Loss
  // Payable" matched none of the three literals it was compared against).
  const designationsOf = ai => (Array.isArray(ai?.Designations) ? ai.Designations : []).filter(Boolean);
  const isOtherNamedInsured = ai => (ai.InterestType || '').toLowerCase() === 'other named insured';

  const propertyInterests = (additionalInterests || []).filter(ai => designationsOf(ai).length > 0);
  const excluded = (additionalInterests || []).filter(ai => designationsOf(ai).length === 0);

  console.log('[ACORD 28] additionalInterests received:', (additionalInterests || []).length,
    'property interests', propertyInterests.length,
    propertyInterests.map(ai => `${ai.EntityName} [${designationsOf(ai).join(', ')}]`));
  // Nothing is dropped silently. Other Named Insured records are expected to
  // be excluded here; anything else with no Designations needs one set.
  for (const ai of excluded) {
    if (isOtherNamedInsured(ai)) {
      console.log(`[ACORD 28] excluded (Other Named Insured, not a property interest): ${ai.EntityName}`);
    } else {
      console.warn(
        `[ACORD 28] excluded from ADDITIONAL INTEREST — no Designations set: ` +
        `"${ai.EntityName}" (InterestType "${ai.InterestType || ''}", id ${ai.id}). ` +
        'Set its Designations to place it on the certificate.'
      );
    }
  }
  const selectedInterest = propertyInterests[0] || {};
  const secondInterest = propertyInterests[1] || {};

  // Find CP coverages for the selected policy
  const policyCoverages = (coverages || []).filter(c =>
    String(c.PolicyId) === String(policy.id) || (c.LOB || '').toLowerCase().includes('property')
  );

  const today = new Date().toISOString().split('T')[0];

  // Form state
  const [form, setForm] = useState({});

  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const p = policy;
    setForm({
      date: today,
      carrier: p.Carrier || '',
      naicCode: p.NAICCode || '',
      policyType: 'Commercial Property',
      policyNumber: p.PolicyNumber || '',
      effectiveDate: p.EffectiveDate || '',
      expirationDate: p.ExpirationDate || '',
      loanNumber: selectedInterest.ReferenceNumber || '',
      additionalNamedInsured: p.AdditionalNamedInsured || '',
      // Property — split address from building record
      coverBuilding: building.CoverBuilding !== undefined ? building.CoverBuilding : true,
      coverBPP: building.CoverBPP !== undefined ? building.CoverBPP : true,
      locAddress: (building.Address || '').split(',')[0]?.trim() || '',
      locAddress2: (building.Address || '').split(',').slice(1).join(',').trim() || '',
      locCity: building.City || '',
      locState: building.State || '',
      locZip: building.Zip || '',
      buildingDescription: building.BuildingDescription || '',
      // Coverage — load from policy record, fall back to building/defaults
      perilsInsured: building.PerilsInsured || 'Special',
      amountOfInsurance: building.BuildingValue || '',
      bppAmount: building.BPPValue || '',
      deductible: p.Deductible ?? '',
      businessIncome: p.BusinessIncome ?? !!building.BusinessIncomeValue,
      businessIncomeLimit: p.BusinessIncomeLimit ?? building.BusinessIncomeValue ?? '',
      businessIncomeType: p.BusinessIncomeType || 'Actual Loss Sustained',
      rentalValue: p.RentalValue ?? false,
      rentalValueLimit: p.RentalValueLimit ?? '',
      blanketCoverage: p.BlanketCoverage || 'N/A',
      terrorismCoverage: p.TerrorismCoverage || 'N/A',
      terrorismExclusion: p.TerrorismExclusion || 'No',
      domesticTerrorismExcluded: p.DomesticTerrorismExcluded || 'No',
      fungusCoverage: p.FungusCoverage || 'N/A',
      fungusLimit: p.FungusLimit ?? '',
      fungusDeductible: p.FungusDeductible ?? '',
      fungusExclusion: p.FungusExclusion || 'No',
      replacementCost: p.ReplacementCost !== undefined ? (p.ReplacementCost ? 'Yes' : 'No') : 'Yes',
      agreedValue: p.AgreedValue || 'N/A',
      coinsurance: p.Coinsurance !== undefined ? (p.Coinsurance ? 'Yes' : 'No') : 'Yes',
      coinsurancePct: p.CoinsurancePercent ?? '',
      equipmentBreakdown: p.EquipmentBreakdown || 'N/A',
      equipmentBreakdownLimit: p.EquipmentBreakdownLimit ?? '',
      equipmentBreakdownDed: p.EquipmentBreakdownDed ?? '',
      ordinanceOrLaw1: p.OrdinanceOrLawA || 'N/A',
      ordinanceOrLaw1Limit: p.OrdinanceOrLawALimit ?? '',
      ordinanceOrLaw1Ded: p.OrdinanceOrLawADed ?? '',
      ordinanceOrLaw2: p.OrdinanceOrLawB || 'N/A',
      ordinanceOrLaw2Limit: p.OrdinanceOrLawBLimit ?? '',
      ordinanceOrLaw2Ded: p.OrdinanceOrLawBDed ?? '',
      ordinanceOrLaw3: p.OrdinanceOrLawC || 'N/A',
      ordinanceOrLaw3Limit: p.OrdinanceOrLawCLimit ?? '',
      ordinanceOrLaw3Ded: p.OrdinanceOrLawCDed ?? '',
      earthMovement: p.EarthMovement || 'N/A',
      earthMovementLimit: p.EarthMovementLimit ?? '',
      earthMovementDed: p.EarthMovementDed ?? '',
      earthMovementDedDesc: p.EarthMovementDedDesc ?? '',
      flood: p.Flood || 'N/A',
      floodLimit: p.FloodLimit ?? '',
      floodDed: p.FloodDed ?? '',
      floodDedDesc: p.FloodDedDesc ?? '',
      windHail: p.WindHail || 'Yes',
      windHailDiffProvisions: p.WindHailDiffProvisions ?? false,
      windHailLimit: p.WindHailLimit ?? '',
      windHailDed: p.WindHailDed ?? '',
      windHailDedDesc: p.WindHailDedDesc ?? '',
      namedStorm: p.NamedStorm || 'No',
      namedStormDiffProvisions: p.NamedStormDiffProvisions ?? false,
      namedStormLimit: p.NamedStormLimit ?? '',
      namedStormDed: p.NamedStormDed ?? '',
      namedStormDedDesc: p.NamedStormDedDesc ?? '',
      permissionToWaiveSubrogation: p.SubrogationWaiver || 'N/A',
      // Additional Interest
      // Designations drive the four ACORD 28 indicator boxes. No fallback:
      // with no matching interest record there is nothing to support a
      // designation, and defaulting put an unsupported LOSS PAYEE check on
      // the certificate. InterestType is carried unchanged for the save.
      designations: Array.isArray(selectedInterest.Designations) ? selectedInterest.Designations : [],
      interestType: selectedInterest.InterestType || '',
      interestName: selectedInterest.EntityName || '',
      interestAddress: selectedInterest.Address || '',
      interestCity: selectedInterest.City || '',
      interestState: selectedInterest.State || '',
      interestZip: selectedInterest.Zip || '',
      lenderServicingAgent: '',
      // Second additional interest
      interest2Name: secondInterest.EntityName || '',
      interest2Address: secondInterest.Address || '',
      interest2City: secondInterest.City || '',
      interest2State: secondInterest.State || '',
      interest2Zip: secondInterest.Zip || '',
    });
    if (onDirtyChange) onDirtyChange(false);
  }, [selectedPolicyIdx, selectedBuildingIdx]);

  async function handleSave() {
    if (!policy.id || !getToken) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const token = await getToken();
      await updateItem(token, FIG_LISTS.POLICIES, policy.id, {
        Carrier: form.carrier,
        NAICCode: form.naicCode,
        PolicyNumber: form.policyNumber,
        EffectiveDate: form.effectiveDate,
        ExpirationDate: form.expirationDate,
        Deductible: form.deductible || null,
        ReplacementCost: form.replacementCost === 'Yes',
        Coinsurance: form.coinsurance === 'Yes',
        CoinsurancePercent: form.coinsurancePct ? Number(form.coinsurancePct) : null,
        BusinessIncome: form.businessIncome,
        BusinessIncomeLimit: form.businessIncomeLimit ? Number(form.businessIncomeLimit) : null,
        BusinessIncomeType: form.businessIncomeType,
        RentalValue: form.rentalValue,
        RentalValueLimit: form.rentalValueLimit ? Number(form.rentalValueLimit) : null,
        BlanketCoverage: form.blanketCoverage,
        TerrorismCoverage: form.terrorismCoverage,
        TerrorismExclusion: form.terrorismExclusion,
        DomesticTerrorismExcluded: form.domesticTerrorismExcluded,
        FungusCoverage: form.fungusCoverage,
        FungusLimit: form.fungusLimit ? Number(form.fungusLimit) : null,
        FungusDeductible: form.fungusDeductible ? Number(form.fungusDeductible) : null,
        FungusExclusion: form.fungusExclusion,
        AgreedValue: form.agreedValue,
        EquipmentBreakdown: form.equipmentBreakdown,
        EquipmentBreakdownLimit: form.equipmentBreakdownLimit ? Number(form.equipmentBreakdownLimit) : null,
        EquipmentBreakdownDed: form.equipmentBreakdownDed ? Number(form.equipmentBreakdownDed) : null,
        OrdinanceOrLawA: form.ordinanceOrLaw1,
        OrdinanceOrLawALimit: form.ordinanceOrLaw1Limit ? Number(form.ordinanceOrLaw1Limit) : null,
        OrdinanceOrLawADed: form.ordinanceOrLaw1Ded ? Number(form.ordinanceOrLaw1Ded) : null,
        OrdinanceOrLawB: form.ordinanceOrLaw2,
        OrdinanceOrLawBLimit: form.ordinanceOrLaw2Limit ? Number(form.ordinanceOrLaw2Limit) : null,
        OrdinanceOrLawBDed: form.ordinanceOrLaw2Ded ? Number(form.ordinanceOrLaw2Ded) : null,
        OrdinanceOrLawC: form.ordinanceOrLaw3,
        OrdinanceOrLawCLimit: form.ordinanceOrLaw3Limit ? Number(form.ordinanceOrLaw3Limit) : null,
        OrdinanceOrLawCDed: form.ordinanceOrLaw3Ded ? Number(form.ordinanceOrLaw3Ded) : null,
        EarthMovement: form.earthMovement,
        EarthMovementLimit: form.earthMovementLimit ? Number(form.earthMovementLimit) : null,
        EarthMovementDed: form.earthMovementDed ? Number(form.earthMovementDed) : null,
        EarthMovementDedDesc: form.earthMovementDedDesc || '',
        Flood: form.flood,
        FloodLimit: form.floodLimit ? Number(form.floodLimit) : null,
        FloodDed: form.floodDed ? Number(form.floodDed) : null,
        FloodDedDesc: form.floodDedDesc || '',
        WindHail: form.windHail,
        WindHailLimit: form.windHailLimit ? Number(form.windHailLimit) : null,
        WindHailDed: form.windHailDed ? Number(form.windHailDed) : null,
        WindHailDedDesc: form.windHailDedDesc || '',
        WindHailDiffProvisions: form.windHailDiffProvisions,
        NamedStorm: form.namedStorm,
        NamedStormLimit: form.namedStormLimit ? Number(form.namedStormLimit) : null,
        NamedStormDed: form.namedStormDed ? Number(form.namedStormDed) : null,
        NamedStormDedDesc: form.namedStormDedDesc || '',
        NamedStormDiffProvisions: form.namedStormDiffProvisions,
        SubrogationWaiver: form.permissionToWaiveSubrogation,
        AdditionalNamedInsured: form.additionalNamedInsured,
      });

      // Save building fields
      if (building.id) {
        await updateItem(token, FIG_LISTS.BUILDINGS, building.id, {
          BuildingDescription: form.buildingDescription,
          CoverBuilding: form.coverBuilding,
          CoverBPP: form.coverBPP,
          PerilsInsured: form.perilsInsured,
          BuildingValue: form.amountOfInsurance ? Number(form.amountOfInsurance) : null,
          BPPValue: form.bppAmount ? Number(form.bppAmount) : null,
        });
      }

      // Save additional interest — the record the form is bound to, which is
      // propertyInterests[0]. Previously this re-ran the old InterestType
      // exact-match here, which no longer selects the same record.
      if (form.interestName && selectedInterest?.id) {
        await updateItem(token, FIG_LISTS.ADDITIONAL_INTERESTS, selectedInterest.id, {
          // InterestType is carried through unchanged — it still marks
          // property interests apart from Other Named Insured records.
          InterestType: form.interestType,
          Designations: form.designations || [],
          EntityName: form.interestName,
          Address: form.interestAddress,
          City: form.interestCity,
          State: form.interestState,
          Zip: form.interestZip,
          LenderServicingAgent: form.lenderServicingAgent,
        });
      }
      setSaveMsg('Saved');
      if (onDirtyChange) onDirtyChange(false);
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) {
      setSaveMsg('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  function set(key, val) {
    setForm(prev => ({ ...prev, [key]: val }));
    if (onDirtyChange) onDirtyChange(true);
  }

  const covRowStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 120px 100px 100px',
    gap: '0.4rem',
    alignItems: 'center',
    padding: '0.3rem 0.35rem',
    borderBottom: '1px solid #ddd',
  };
  const covRowNoLimitStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 120px',
    gap: '0.4rem',
    alignItems: 'center',
    padding: '0.3rem 0.35rem',
    borderBottom: '1px solid #ddd',
  };

  function renderYNN(key, label) {
    return (
      <div style={covRowNoLimitStyle}>
        <span className="acord-label" style={{ whiteSpace: 'normal' }}>{label}</span>
        <div className="acord-checkbox-row" style={{ gap: '0.4rem' }}>
          {YNN.filter(Boolean).map(v => (
            <label className="acord-checkbox" key={v}>
              <input type="radio" name={key} checked={form[key] === v} onChange={() => set(key, v)} />
              <span>{v}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  function renderYNNWithFields(key, label, limitKey, dedKey, dedDescKey) {
    return (
      <div style={covRowStyle}>
        <span className="acord-label" style={{ whiteSpace: 'normal' }}>{label}</span>
        <div className="acord-checkbox-row" style={{ gap: '0.4rem' }}>
          {YNN.filter(Boolean).map(v => (
            <label className="acord-checkbox" key={v}>
              <input type="radio" name={key} checked={form[key] === v} onChange={() => set(key, v)} />
              <span>{v}</span>
            </label>
          ))}
        </div>
        {limitKey ? (
          <input className="acord-input" type="number" placeholder="Limit"
            value={form[limitKey] || ''} onChange={e => set(limitKey, e.target.value)} />
        ) : <span />}
        {dedKey ? (
          <input className="acord-input" type="number" placeholder="$ Ded"
            value={form[dedKey] || ''} onChange={e => set(dedKey, e.target.value)} />
        ) : <span />}
        {dedDescKey && (
          <input className="acord-input" placeholder="or % Ded text (e.g. 3% TIV, $100k min)"
            value={form[dedDescKey] || ''} onChange={e => set(dedDescKey, e.target.value)}
            title="If filled, this overrides the dollar deductible on the ACORD 28 output." />
        )}
      </div>
    );
  }

  return (
    <div className="acord-form">
      {/* HEADER */}
      <div className="acord-header">
        <div className="acord-header-left">
          <span className="acord-logo">ACORD</span>
          <span className="acord-form-title">ACORD 28 &mdash; EVIDENCE OF COMMERCIAL PROPERTY INSURANCE</span>
        </div>
        <div className="acord-header-right">
          <label className="acord-hdr-field">
            <span className="acord-hdr-label">DATE</span>
            <input className="acord-input" type="date" value={form.date || ''} onChange={e => set('date', e.target.value)}
              style={{ background: 'transparent', color: '#fff', border: 'none', borderBottom: '1px solid #7eb8da', fontSize: '0.8rem' }} />
          </label>
        </div>
      </div>

      {/* Policy / Building selectors */}
      {(cpPolicies.length > 1 || (buildings || []).length > 1) && (
        <div className="acord-section">
          <div className="acord-section-bar">SELECT POLICY & BUILDING</div>
          <div className="acord-section-body">
            <div className="acord-row">
              {cpPolicies.length > 0 && (
                <label className="acord-cell">
                  <span className="acord-label">CP POLICY</span>
                  <select className="acord-input" value={selectedPolicyIdx} onChange={e => setSelectedPolicyIdx(Number(e.target.value))}>
                    {cpPolicies.map((p, i) => (
                      <option key={p.id} value={i}>{p.Carrier || 'Policy'} — {p.PolicyNumber || '(no #)'}</option>
                    ))}
                  </select>
                </label>
              )}
              {(buildings || []).length > 0 && (
                <label className="acord-cell">
                  <span className="acord-label">BUILDING</span>
                  <select className="acord-input" value={selectedBuildingIdx} onChange={e => setSelectedBuildingIdx(Number(e.target.value))}>
                    {(buildings || []).map((b, i) => (
                      <option key={b.id || i} value={i}>{b.Address || `Building ${i + 1}`} — {b.BuildingDescription || ''}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PRODUCER */}
      <div className="acord-section">
        <div className="acord-section-bar">PRODUCER</div>
        <div className="acord-section-body">
          <div className="acord-row">
            <div className="acord-cell acord-cell--2">
              <span className="acord-label">AGENCY</span>
              <input className="acord-input" value="Fragos Insurance Group LLC" readOnly />
            </div>
          </div>
          <div className="acord-row">
            <div className="acord-cell"><span className="acord-label">ADDRESS</span><input className="acord-input" value="16915 Walden Rd Suite #312, Montgomery, TX 77356" readOnly /></div>
            <div className="acord-cell acord-cell--sm"><span className="acord-label">PHONE</span><input className="acord-input" value="(936) 755-2521" readOnly /></div>
          </div>
          <div className="acord-row">
            <div className="acord-cell"><span className="acord-label">EMAIL</span><input className="acord-input" value="marc@myfiginsurance.com" readOnly /></div>
          </div>
        </div>
      </div>

      {/* COMPANY / POLICY */}
      <div className="acord-section">
        <div className="acord-section-bar">COMPANY / POLICY</div>
        <div className="acord-section-body">
          <div className="acord-row">
            <label className="acord-cell acord-cell--2"><span className="acord-label">COMPANY NAME</span>
              <input className="acord-input" value={form.carrier} onChange={e => set('carrier', e.target.value)} /></label>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">NAIC NO.</span>
              <input className="acord-input" value={form.naicCode} onChange={e => set('naicCode', e.target.value)} /></label>
          </div>
          <div className="acord-row">
            <label className="acord-cell"><span className="acord-label">POLICY TYPE</span>
              <input className="acord-input" value={form.policyType} onChange={e => set('policyType', e.target.value)} /></label>
            <label className="acord-cell"><span className="acord-label">POLICY NUMBER</span>
              <input className="acord-input" value={form.policyNumber} onChange={e => set('policyNumber', e.target.value)} /></label>
          </div>
          <div className="acord-row">
            <label className="acord-cell"><span className="acord-label">EFFECTIVE DATE</span>
              <input className="acord-input" value={form.effectiveDate} onChange={e => set('effectiveDate', e.target.value)} /></label>
            <label className="acord-cell"><span className="acord-label">EXPIRATION DATE</span>
              <input className="acord-input" value={form.expirationDate} onChange={e => set('expirationDate', e.target.value)} /></label>
            <label className="acord-cell"><span className="acord-label">LOAN NUMBER</span>
              <input className="acord-input" value={form.loanNumber} onChange={e => set('loanNumber', e.target.value)} /></label>
          </div>
        </div>
      </div>

      {/* NAMED INSURED */}
      <div className="acord-section">
        <div className="acord-section-bar">NAMED INSURED</div>
        <div className="acord-section-body">
          <div className="acord-row">
            <div className="acord-cell acord-cell--2">
              <span className="acord-label">FIRST NAMED INSURED</span>
              <input className="acord-input" value={insured?.CompanyName || ''} readOnly />
            </div>
          </div>
          <div className="acord-row">
            <div className="acord-cell"><span className="acord-label">ADDRESS</span>
              <input className="acord-input" value={`${insured?.MailingAddress || ''}, ${insured?.MailingCity || ''}, ${insured?.MailingState || ''} ${insured?.MailingZip || ''}`} readOnly /></div>
          </div>
          <div className="acord-row">
            <label className="acord-cell acord-cell--2"><span className="acord-label">ADDITIONAL NAMED INSURED</span>
              <input className="acord-input" value={form.additionalNamedInsured} onChange={e => set('additionalNamedInsured', e.target.value)} /></label>
          </div>
        </div>
      </div>

      {/* PROPERTY INFORMATION */}
      <div className="acord-section">
        <div className="acord-section-bar">PROPERTY INFORMATION</div>
        <div className="acord-section-body">
          <div className="acord-row">
            <div className="acord-cell" style={{ flexDirection: 'row', gap: '1rem', alignItems: 'center' }}>
              <label className="acord-checkbox">
                <input type="checkbox" checked={form.coverBuilding} onChange={e => set('coverBuilding', e.target.checked)} />
                <span>Building</span>
              </label>
              <label className="acord-checkbox">
                <input type="checkbox" checked={form.coverBPP} onChange={e => set('coverBPP', e.target.checked)} />
                <span>Business Personal Property</span>
              </label>
            </div>
          </div>
          <div className="acord-row">
            <label className="acord-cell acord-cell--2"><span className="acord-label">STREET ADDRESS</span>
              <input className="acord-input" value={form.locAddress} onChange={e => set('locAddress', e.target.value)} /></label>
          </div>
          <div className="acord-row">
            <label className="acord-cell"><span className="acord-label">SUITE / UNIT</span>
              <input className="acord-input" value={form.locAddress2} onChange={e => set('locAddress2', e.target.value)} /></label>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">CITY</span>
              <input className="acord-input" value={form.locCity} onChange={e => set('locCity', e.target.value)} /></label>
            <label className="acord-cell acord-cell--xs"><span className="acord-label">STATE</span>
              <input className="acord-input" value={form.locState} onChange={e => set('locState', e.target.value)} maxLength={2} /></label>
            <label className="acord-cell acord-cell--xs"><span className="acord-label">ZIP</span>
              <input className="acord-input" value={form.locZip} onChange={e => set('locZip', e.target.value)} /></label>
          </div>
          <div className="acord-row">
            <label className="acord-cell acord-cell--2"><span className="acord-label">DESCRIPTION OF PROPERTY</span>
              <input className="acord-input" value={form.buildingDescription} onChange={e => set('buildingDescription', e.target.value)} /></label>
          </div>
        </div>
      </div>

      {/* COVERAGE INFORMATION */}
      <div className="acord-section">
        <div className="acord-section-bar">COVERAGE INFORMATION</div>
        <div className="acord-section-body">
          {/* Perils Insured */}
          <div className="acord-row">
            <div className="acord-cell"><span className="acord-label">PERILS INSURED</span>
              <div className="acord-checkbox-row">
                {['Basic', 'Broad', 'Special'].map(v => (
                  <label className="acord-checkbox" key={v}>
                    <input type="radio" name="perils" checked={form.perilsInsured === v} onChange={() => set('perilsInsured', v)} />
                    <span>{v}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Amounts */}
          <div className="acord-row">
            <label className="acord-cell"><span className="acord-label">BUILDING AMOUNT OF INSURANCE</span>
              <input className="acord-input" type="number" value={form.amountOfInsurance} onChange={e => set('amountOfInsurance', e.target.value)} /></label>
            <label className="acord-cell"><span className="acord-label">BPP AMOUNT</span>
              <input className="acord-input" type="number" value={form.bppAmount} onChange={e => set('bppAmount', e.target.value)} /></label>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">DEDUCTIBLE</span>
              <input className="acord-input" value={form.deductible} onChange={e => set('deductible', e.target.value)} /></label>
          </div>

          {/* Business Income */}
          <div className="acord-row">
            <div className="acord-cell" style={{ flexDirection: 'row', gap: '0.75rem', alignItems: 'center' }}>
              <label className="acord-checkbox">
                <input type="checkbox" checked={form.businessIncome} onChange={e => set('businessIncome', e.target.checked)} />
                <span>Business Income</span>
              </label>
            </div>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">LIMIT</span>
              <input className="acord-input" type="number" value={form.businessIncomeLimit} onChange={e => set('businessIncomeLimit', e.target.value)} /></label>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">TYPE</span>
              <select className="acord-input" value={form.businessIncomeType} onChange={e => set('businessIncomeType', e.target.value)}>
                <option value="Actual Loss Sustained">Actual Loss Sustained</option>
                <option value="12 Months">12 Months</option>
                <option value="18 Months">18 Months</option>
                <option value="24 Months">24 Months</option>
              </select>
            </label>
          </div>

          {/* Rental Value */}
          <div className="acord-row">
            <div className="acord-cell" style={{ flexDirection: 'row', gap: '0.75rem', alignItems: 'center' }}>
              <label className="acord-checkbox">
                <input type="checkbox" checked={form.rentalValue} onChange={e => set('rentalValue', e.target.checked)} />
                <span>Rental Value</span>
              </label>
            </div>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">LIMIT</span>
              <input className="acord-input" type="number" value={form.rentalValueLimit} onChange={e => set('rentalValueLimit', e.target.value)} /></label>
          </div>

          {/* Yes/No/N/A fields */}
          {renderYNN('blanketCoverage', 'BLANKET COVERAGE')}
          {renderYNN('terrorismCoverage', 'TERRORISM COVERAGE')}
          {renderYNN('terrorismExclusion', 'TERRORISM EXCLUSION')}
          {renderYNN('domesticTerrorismExcluded', 'DOMESTIC TERRORISM EXCLUDED')}
          {renderYNNWithFields('fungusCoverage', 'LIMITED FUNGUS COVERAGE', 'fungusLimit', 'fungusDeductible')}
          {renderYNN('fungusExclusion', 'FUNGUS EXCLUSION')}
          {renderYNN('replacementCost', 'REPLACEMENT COST')}
          {renderYNN('agreedValue', 'AGREED VALUE')}

          {/* Coinsurance */}
          <div className="acord-row">
            <div className="acord-cell"><span className="acord-label">COINSURANCE</span>
              <div className="acord-checkbox-row">
                {YNN.filter(Boolean).map(v => (
                  <label className="acord-checkbox" key={v}>
                    <input type="radio" name="coinsurance" checked={form.coinsurance === v} onChange={() => set('coinsurance', v)} />
                    <span>{v}</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="acord-cell acord-cell--xs"><span className="acord-label">%</span>
              <input className="acord-input" value={form.coinsurancePct} onChange={e => set('coinsurancePct', e.target.value)} /></label>
          </div>

          {renderYNNWithFields('equipmentBreakdown', 'EQUIPMENT BREAKDOWN', 'equipmentBreakdownLimit', 'equipmentBreakdownDed')}

          {/* Ordinance or Law — 3 lines */}
          {renderYNNWithFields('ordinanceOrLaw1', 'ORDINANCE OR LAW — Coverage A', 'ordinanceOrLaw1Limit', 'ordinanceOrLaw1Ded')}
          {renderYNNWithFields('ordinanceOrLaw2', 'ORDINANCE OR LAW — Coverage B', 'ordinanceOrLaw2Limit', 'ordinanceOrLaw2Ded')}
          {renderYNNWithFields('ordinanceOrLaw3', 'ORDINANCE OR LAW — Coverage C', 'ordinanceOrLaw3Limit', 'ordinanceOrLaw3Ded')}

          {renderYNNWithFields('earthMovement', 'EARTH MOVEMENT', 'earthMovementLimit', 'earthMovementDed', 'earthMovementDedDesc')}
          {renderYNNWithFields('flood', 'FLOOD', 'floodLimit', 'floodDed', 'floodDedDesc')}

          {/* Wind/Hail */}
          <div className="acord-row">
            <div className="acord-cell">
              <span className="acord-label">WIND / HAIL</span>
              <div className="acord-checkbox-row">
                {['Yes', 'No'].map(v => (
                  <label className="acord-checkbox" key={v}>
                    <input type="radio" name="windHail" checked={form.windHail === v} onChange={() => set('windHail', v)} />
                    <span>{v}</span>
                  </label>
                ))}
                <label className="acord-checkbox">
                  <input type="checkbox" checked={form.windHailDiffProvisions} onChange={e => set('windHailDiffProvisions', e.target.checked)} />
                  <span>Subject to Different Provisions</span>
                </label>
              </div>
            </div>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">LIMIT</span>
              <input className="acord-input" type="number" value={form.windHailLimit} onChange={e => set('windHailLimit', e.target.value)} /></label>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">$ DED</span>
              <input className="acord-input" type="number" value={form.windHailDed} onChange={e => set('windHailDed', e.target.value)} /></label>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">% DED TEXT</span>
              <input className="acord-input" placeholder="e.g. 3% TIV, $100k min"
                value={form.windHailDedDesc} onChange={e => set('windHailDedDesc', e.target.value)}
                title="If filled, this overrides the dollar deductible on the ACORD 28 output." /></label>
          </div>

          {/* Named Storm */}
          <div className="acord-row">
            <div className="acord-cell">
              <span className="acord-label">NAMED STORM</span>
              <div className="acord-checkbox-row">
                {['Yes', 'No'].map(v => (
                  <label className="acord-checkbox" key={v}>
                    <input type="radio" name="namedStorm" checked={form.namedStorm === v} onChange={() => set('namedStorm', v)} />
                    <span>{v}</span>
                  </label>
                ))}
                <label className="acord-checkbox">
                  <input type="checkbox" checked={form.namedStormDiffProvisions} onChange={e => set('namedStormDiffProvisions', e.target.checked)} />
                  <span>Subject to Different Provisions</span>
                </label>
              </div>
            </div>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">LIMIT</span>
              <input className="acord-input" type="number" value={form.namedStormLimit} onChange={e => set('namedStormLimit', e.target.value)} /></label>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">$ DED</span>
              <input className="acord-input" type="number" value={form.namedStormDed} onChange={e => set('namedStormDed', e.target.value)} /></label>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">% DED TEXT</span>
              <input className="acord-input" placeholder="e.g. 5% TIV, $250k min"
                value={form.namedStormDedDesc} onChange={e => set('namedStormDedDesc', e.target.value)}
                title="If filled, this overrides the dollar deductible on the ACORD 28 output." /></label>
          </div>

          {renderYNN('permissionToWaiveSubrogation', 'PERMISSION TO WAIVE SUBROGATION')}
        </div>
      </div>

      {/* ADDITIONAL INTEREST */}
      <div className="acord-section">
        <div className="acord-section-bar">ADDITIONAL INTEREST</div>
        <div className="acord-section-body">
          <div className="acord-row">
            <div className="acord-cell acord-cell--2">
              <span className="acord-label">INTEREST TYPE</span>
              <div className="acord-checkbox-row acord-checkbox-row--wrap">
                {['Contract of Sale', 'Lender\'s Loss Payable', 'Loss Payee', 'Mortgagee'].map(t => (
                  <label className="acord-checkbox" key={t}>
                    <input
                      type="checkbox"
                      checked={(form.designations || []).includes(t)}
                      onChange={e => {
                        const current = form.designations || [];
                        set('designations', e.target.checked
                          ? [...current, t]
                          : current.filter(d => d !== t));
                      }}
                    />
                    <span>{t}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="acord-row">
            <label className="acord-cell acord-cell--2"><span className="acord-label">NAME</span>
              <input className="acord-input" value={form.interestName} onChange={e => set('interestName', e.target.value)} /></label>
          </div>
          <div className="acord-row">
            <label className="acord-cell"><span className="acord-label">ADDRESS</span>
              <input className="acord-input" value={form.interestAddress} onChange={e => set('interestAddress', e.target.value)} /></label>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">CITY</span>
              <input className="acord-input" value={form.interestCity} onChange={e => set('interestCity', e.target.value)} /></label>
            <label className="acord-cell acord-cell--xs"><span className="acord-label">STATE</span>
              <input className="acord-input" value={form.interestState} onChange={e => set('interestState', e.target.value)} maxLength={2} /></label>
            <label className="acord-cell acord-cell--xs"><span className="acord-label">ZIP</span>
              <input className="acord-input" value={form.interestZip} onChange={e => set('interestZip', e.target.value)} /></label>
          </div>
          <div className="acord-row">
            <label className="acord-cell acord-cell--2"><span className="acord-label">LENDER SERVICING AGENT</span>
              <input className="acord-input" value={form.lenderServicingAgent} onChange={e => set('lenderServicingAgent', e.target.value)} /></label>
          </div>
          {(form.interest2Name || secondInterest.EntityName) && (
            <>
              <div style={{ borderTop: '1px solid #ddd', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
                <span className="acord-label" style={{ fontWeight: 700, color: '#4B0082' }}>SECOND ADDITIONAL INTEREST</span>
              </div>
              <div className="acord-row">
                <label className="acord-cell acord-cell--2"><span className="acord-label">NAME</span>
                  <input className="acord-input" value={form.interest2Name} onChange={e => set('interest2Name', e.target.value)} /></label>
              </div>
              <div className="acord-row">
                <label className="acord-cell"><span className="acord-label">ADDRESS</span>
                  <input className="acord-input" value={form.interest2Address} onChange={e => set('interest2Address', e.target.value)} /></label>
                <label className="acord-cell acord-cell--sm"><span className="acord-label">CITY</span>
                  <input className="acord-input" value={form.interest2City} onChange={e => set('interest2City', e.target.value)} /></label>
                <label className="acord-cell acord-cell--xs"><span className="acord-label">STATE</span>
                  <input className="acord-input" value={form.interest2State} onChange={e => set('interest2State', e.target.value)} maxLength={2} /></label>
                <label className="acord-cell acord-cell--xs"><span className="acord-label">ZIP</span>
                  <input className="acord-input" value={form.interest2Zip} onChange={e => set('interest2Zip', e.target.value)} /></label>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="acord-actions">
        <button className="FIG-back-btn" onClick={onBack}>&lsaquo; Back to ACORDs</button>
        <button className="FIG-save-btn" onClick={handleSave} disabled={saving || !policy.id}>
          {saving ? 'Saving...' : 'Save Coverage'}
        </button>
        {saveMsg && <span className={'FIG-save-msg' + (saveMsg.startsWith('Error') ? ' FIG-save-msg--error' : '')}>{saveMsg}</span>}
        <button className="FIG-save-btn acord-pdf-btn" disabled={generating} onClick={async () => {
          setGenerating(true);
          try {
            await generateACORD28(form, insured, getToken);
          } catch (err) {
            console.error('[ACORD 28] PDF generation failed:', err);
            alert('Error generating PDF: ' + err.message);
          } finally {
            setGenerating(false);
          }
        }}>
          {generating ? 'Generating...' : 'Generate PDF'}
        </button>
      </div>
    </div>
  );
}

export default ACORD28;
